const money = (v) => (v === null || v === undefined ? '—' : `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const NAMES = { conservative: 'Conservative', expected: 'Expected', optimistic: 'Optimistic' };

const Stat = ({ label, value, note, testid }) => (
  <div className="rounded-xl bg-ivory text-matte p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-matte/50">{label}</div>
    <div className="mt-1 font-serif-display text-2xl" data-testid={testid}>{value}</div>
    {note && <div className="text-[11px] text-matte/50 mt-1">{note}</div>}
  </div>
);

export const FinancialPanel = ({ financials, updating }) => {
  const r = financials.results;
  const rows = [['sales', 'Sales per month', (v) => v], ['revenue', 'Revenue', money], ['direct_costs', 'Direct costs', money],
    ['gross_profit', 'Gross profit', money], ['recurring', 'Fixed + set-aside', money], ['net', 'Estimated net profit', money]];
  return (
    <section className="mt-6 rounded-2xl bg-matte text-ivory p-6 md:p-8" data-testid="financial-panel" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Financial plan · estimates</div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-ivory/50">{updating ? 'Updating…' : 'Based on your saved answers'}</span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Startup costs" value={money(r.startup_total)} note="sum of one-time costs" testid="fin-startup-total" />
        <Stat label="Cash after startup" value={money(r.cash_after_startup)} note="available cash − startup costs" testid="fin-cash-after" />
        <Stat label="Funding gap" value={money(r.funding_gap)} note="startup costs not covered by cash" testid="fin-funding-gap" />
        <Stat label="Break-even" value={r.break_even_sales ?? '—'} note="(fixed + set-aside) ÷ gross profit per sale, rounded up" testid="fin-break-even" />
      </div>
      {r.scenarios.length > 0 && (
        <div className="mt-5 overflow-x-auto rounded-xl bg-ivory text-matte">
          <table className="w-full text-sm" data-testid="fin-scenarios">
            <thead><tr className="text-[10px] uppercase tracking-[0.2em] text-matte/50 text-left">
              <th className="p-3">Per month</th>{r.scenarios.map((s) => <th key={s.name} className="p-3 text-right">{NAMES[s.name]} ({s.pct}%)</th>)}
            </tr></thead>
            <tbody>
              {rows.map(([k, label, f]) => (
                <tr key={k} className="border-t border-matte/10">
                  <td className="p-3">{label}</td>
                  {r.scenarios.map((s) => <td key={s.name} className="p-3 text-right font-medium" data-testid={`fin-${s.name}-${k}`}>{f(s[k])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.cash_flow.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-ivory/60">Six-month cash balance (expected scenario)</div>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="fin-cash-flow">
            {r.cash_flow.map((m) => (
              <div key={m.month} className="rounded-lg bg-ivory/10 p-3 text-center">
                <div className="text-[10px] text-ivory/50">Month {m.month}</div>
                <div className={`text-sm ${m.balance < 0 ? 'text-red-300' : 'text-ivory'}`} data-testid={`fin-cash-m${m.month}`}>{money(m.balance)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-6 text-sm text-ivory/80">
        <span data-testid="fin-recover">Months to recover startup costs: {r.months_to_recover_startup ?? '—'}</span>
        <span data-testid="fin-runway">Runway if losing money: {r.runway_months ?? '—'}{r.runway_months !== null ? ' months' : ''}</span>
      </div>
      {financials.warnings.length > 0 && (
        <ul className="mt-5 space-y-2 text-sm text-ivory/80" data-testid="fin-warnings">
          {financials.warnings.map((w) => <li key={w} className="flex gap-2"><span className="text-gold">•</span>{w}</li>)}
        </ul>
      )}
      <p className="mt-6 text-[12px] leading-relaxed text-ivory/60" data-testid="fin-disclaimer">
        Educational estimates calculated only from the numbers you entered. They are not forecasts or guarantees. Estimated net profit is before income taxes and any costs you haven&rsquo;t entered; the cash balance assumes every month matches the expected scenario.
      </p>
    </section>
  );
};
